/**
 * Redux duck for Ada registration notification state.
 * Used to track when Ada has found business information that needs user review.
 *
 * Persistence strategy:
 * - Local state (localStorage) for immediate UI responsiveness during registration
 * - Backend state (AdaRegistrationLog.pending_review) for persistence across logout/login
 * - On login, frontend checks backend and syncs local state
 */
import { USER_LOGOUT } from 'react-admin'
import appStorage from 'storage/appStorage'

const SET_ADA_NOTIFICATION_PENDING = 'adaNotification/SET_PENDING' as const
const SET_ADA_NOTIFICATION_WITH_DATA = 'adaNotification/SET_WITH_DATA' as const
const CLEAR_ADA_NOTIFICATION = 'adaNotification/CLEAR' as const

const STORAGE_KEY = 'ada_notification_pending'

export interface AdaStepData {
  key: string
  status: string
  data: Record<string, any>
}

export interface AdaNotificationState {
  hasPendingReview: boolean
  sessionId: string | null
  extractedData: AdaStepData[] | null
}

type AdaNotificationAction =
  | { type: typeof SET_ADA_NOTIFICATION_PENDING; payload: { sessionId: string } }
  | { type: typeof SET_ADA_NOTIFICATION_WITH_DATA; payload: { sessionId: string; extractedData: AdaStepData[] } }
  | { type: typeof CLEAR_ADA_NOTIFICATION }
  | { type: typeof USER_LOGOUT }

const getInitialState = (): AdaNotificationState => {
  try {
    const stored = appStorage.getJSON<AdaNotificationState>(STORAGE_KEY)
    if (stored) {
      return {
        hasPendingReview: stored.hasPendingReview || false,
        sessionId: stored.sessionId || null,
        extractedData: stored.extractedData || null,
      }
    }
  } catch (error) {
    console.error('[adaNotification] Failed to load from appStorage:', error)
  }
  return {
    hasPendingReview: false,
    sessionId: null,
    extractedData: null,
  }
}

const persistState = (state: AdaNotificationState) => {
  try {
    appStorage.setJSON(STORAGE_KEY, state)
  } catch (error) {
    console.error('[adaNotification] Failed to persist to appStorage:', error)
  }
}

export default function adaNotificationReducer(
  state: AdaNotificationState = getInitialState(),
  action: AdaNotificationAction
): AdaNotificationState {
  switch (action.type) {
    case SET_ADA_NOTIFICATION_PENDING: {
      const newState: AdaNotificationState = {
        hasPendingReview: true,
        sessionId: action.payload.sessionId,
        extractedData: null,
      }
      persistState(newState)
      return newState
    }
    case SET_ADA_NOTIFICATION_WITH_DATA: {
      const newState: AdaNotificationState = {
        hasPendingReview: true,
        sessionId: action.payload.sessionId,
        extractedData: action.payload.extractedData,
      }
      persistState(newState)
      return newState
    }
    case CLEAR_ADA_NOTIFICATION:
    case USER_LOGOUT: {
      const newState: AdaNotificationState = {
        hasPendingReview: false,
        sessionId: null,
        extractedData: null,
      }
      persistState(newState)
      return newState
    }
    default:
      return state
  }
}

export const adaNotificationActions = {
  setPending: (sessionId: string) => ({
    type: SET_ADA_NOTIFICATION_PENDING,
    payload: { sessionId },
  }),
  setPendingWithData: (sessionId: string, extractedData: AdaStepData[]) => ({
    type: SET_ADA_NOTIFICATION_WITH_DATA,
    payload: { sessionId, extractedData },
  }),
  clear: () => ({
    type: CLEAR_ADA_NOTIFICATION,
  }),
}

export const adaNotificationSelectors = {
  getHasPendingReview: (state: { adaNotification: AdaNotificationState }) =>
    state.adaNotification?.hasPendingReview || false,
  getSessionId: (state: { adaNotification: AdaNotificationState }) => state.adaNotification?.sessionId || null,
  getExtractedData: (state: { adaNotification: AdaNotificationState }) => state.adaNotification?.extractedData || null,
}
